type Listener = (...args: unknown[]) => void;

export class AudioPlayerService {
  private audio: HTMLAudioElement | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private pendingSrc: string | null = null;
  private recoveryAttempts = 0;
  private lastPlayedTime = 0;

  attach(el: HTMLAudioElement | null) {
    this.audio = el;
    if (!this.audio) return;

    if (this.pendingSrc) {
      this.audio.src = this.pendingSrc;
      this.pendingSrc = null;
    }

    const forward = (evName: string, arg?: unknown) => {
      this.emit(evName, arg);
    };

    const onTime = () => {
      if (this.audio) {
        this.lastPlayedTime = this.audio.currentTime;
      }
      forward("timeupdate", this.audio?.currentTime ?? 0);
    };
    const onLoaded = () => {
      this.recoveryAttempts = 0; // Reset recovery attempts on successful load
      forward("duration", this.audio?.duration ?? 0);
    };
    const onPlay = () => forward("play");
    const onEnded = () => forward("ended");
    const onVolume = () => forward("volumechange", this.audio?.volume ?? 1);

    // Stall recovery — attempt reload and resume if playback stalls
    const onStalled = () => {
      if (!this.audio) return;
      if (this.recoveryAttempts < 3) {
        this.recoveryAttempts++;
        const wasPlaying = !this.audio.paused;
        this.audio.load();
        if (wasPlaying) {
          this.audio.currentTime = this.lastPlayedTime;
          this.audio.play().catch(() => {});
        }
      }
    };

    const onSuspend = () => {
      // Browser may suspend loading prematurely
      if (!this.audio || this.audio.paused) return;
      // If audio was playing but got suspended, try to continue
      const wasPlaying = !this.audio.paused;
      if (wasPlaying && this.audio.readyState < 3) {
        setTimeout(() => {
          if (this.audio && !this.audio.paused && this.audio.readyState < 3) {
            onStalled();
          }
        }, 500);
      }
    };

    const onError = () => {
      if (!this.audio?.error) return;
      forward("error", this.audio.error);
    };

    const onWaiting = () => {};

    const onCanPlayThrough = () => {
      // Audio has buffered enough to play through
      this.recoveryAttempts = 0;
    };

    const onAbort = () => {};

    const onEmptied = () => {};

    const onLoadedData = () => {};

    const onPause = () => {
      forward("pause");
    };

    this.audio.addEventListener("timeupdate", onTime);
    this.audio.addEventListener("loadedmetadata", onLoaded);
    this.audio.addEventListener("loadeddata", onLoadedData);
    this.audio.addEventListener("canplaythrough", onCanPlayThrough);
    this.audio.addEventListener("play", onPlay);
    this.audio.addEventListener("pause", onPause);
    this.audio.addEventListener("ended", onEnded);
    this.audio.addEventListener("volumechange", onVolume);
    this.audio.addEventListener("stalled", onStalled);
    this.audio.addEventListener("suspend", onSuspend);
    this.audio.addEventListener("error", onError);
    this.audio.addEventListener("waiting", onWaiting);
    this.audio.addEventListener("abort", onAbort);
    this.audio.addEventListener("emptied", onEmptied);

    // playback-state reflection to Media Session is handled by a separate service

    // store cleanup on the element so detach can remove them
    (
      this.audio as HTMLAudioElement & {
        __audioPlayerServiceCleanup?: () => void;
      }
    ).__audioPlayerServiceCleanup = () => {
      try {
        this.audio?.removeEventListener("timeupdate", onTime);
        this.audio?.removeEventListener("loadedmetadata", onLoaded);
        this.audio?.removeEventListener("loadeddata", onLoadedData);
        this.audio?.removeEventListener("canplaythrough", onCanPlayThrough);
        this.audio?.removeEventListener("play", onPlay);
        this.audio?.removeEventListener("pause", onPause);
        this.audio?.removeEventListener("ended", onEnded);
        this.audio?.removeEventListener("volumechange", onVolume);
        this.audio?.removeEventListener("stalled", onStalled);
        this.audio?.removeEventListener("suspend", onSuspend);
        this.audio?.removeEventListener("error", onError);
        this.audio?.removeEventListener("waiting", onWaiting);
        this.audio?.removeEventListener("abort", onAbort);
        this.audio?.removeEventListener("emptied", onEmptied);
      } catch (e) {
        // ignore
      }
    };
  }

  detach() {
    const el = this.audio as
      | (HTMLAudioElement & { __audioPlayerServiceCleanup?: () => void })
      | null;
    if (el?.__audioPlayerServiceCleanup) {
      el.__audioPlayerServiceCleanup();
    }
    this.audio = null;
  }

  setSource(src: string | null) {
    if (!src) {
      if (this.audio) {
        this.audio.removeAttribute("src");
        this.audio.load();
      } else {
        this.pendingSrc = null;
      }
      this.recoveryAttempts = 0;
      this.lastPlayedTime = 0;
      this.emit("sourcechange", null);
      return;
    }

    if (this.audio) {
      this.audio.src = src;
      // Force preload to ensure enough data is buffered before playback
      this.audio.preload = "auto";
      this.audio.load();
      this.recoveryAttempts = 0;
      this.lastPlayedTime = 0;
      this.emit("sourcechange", src);
    } else {
      this.pendingSrc = src;
      this.emit("sourcechange", src);
    }
  }

  async play() {
    if (!this.audio) return;

    // Ensure we have enough data before attempting to play
    if (this.audio.readyState < 2) {
      // Wait for loadeddata event (readyState >= 2) with timeout
      await new Promise<void>((resolve) => {
        if (!this.audio) {
          resolve();
          return;
        }

        if (this.audio.readyState >= 2) {
          resolve();
          return;
        }

        const onReady = () => {
          cleanup();
          resolve();
        };

        const timeout = setTimeout(() => {
          cleanup();
          resolve();
        }, 2000);

        const cleanup = () => {
          this.audio?.removeEventListener("loadeddata", onReady);
          this.audio?.removeEventListener("canplay", onReady);
          clearTimeout(timeout);
        };

        this.audio.addEventListener("loadeddata", onReady, { once: true });
        this.audio.addEventListener("canplay", onReady, { once: true });
      });
    }

    try {
      await this.audio.play();
    } catch (e) {
      // Ignore AbortError which occurs when a new load/request interrupts play()
      // (e.g. setSource called while play is pending). This is benign and
      // expected in some autoplay scenarios and should not spam the console.
      try {
        const err = e as { name?: string };
        if (
          err &&
          (err.name === "AbortError" ||
            String(err).includes("interrupted by a new load request"))
        ) {
          return;
        }
        if (err && err.name === "NotAllowedError") {
          // This can occur when autoplay is blocked by the browser. It's not actionable for the user, so we can ignore it.
          return;
        }
      } catch (_) {
        // fallthrough to logging
      }
      // ignore
    }
  }

  pause() {
    this.audio?.pause();
  }

  stop() {
    if (!this.audio) return;
    this.audio.pause();
    try {
      this.audio.currentTime = 0;
    } catch (_e) {
      /* ignore */
    }
  }

  seek(time: number) {
    if (!this.audio) return;
    try {
      this.audio.currentTime = time;
    } catch (_e) {
      /* ignore */
    }
  }

  setVolume(v: number) {
    if (!this.audio) return;
    this.audio.volume = Math.max(0, Math.min(1, v));
  }

  getCurrentTime() {
    return this.audio?.currentTime ?? 0;
  }

  getDuration() {
    return this.audio?.duration ?? 0;
  }

  skip(seconds: number) {
    if (!this.audio) return;
    try {
      const target = Math.max(
        0,
        Math.min(
          this.getDuration() || Infinity,
          (this.audio.currentTime || 0) + seconds,
        ),
      );
      this.audio.currentTime = target;
      this.emit("timeupdate", this.getCurrentTime());
    } catch (_e) {
      /* ignore */
    }
  }

  /**
   * Returns the current audio source (URL) the player is using, or any pending src.
   */
  getSource() {
    return this.audio?.src ?? this.pendingSrc ?? null;
  }

  /**
   * Compare a target source with the current player source in a forgiving way.
   * Returns true when the two refer to the same resource (exact match or one endsWith the other).
   */
  isSameSource(target: string | null) {
    if (!target) return false;
    const cur = this.getSource();
    if (!cur) return false;
    if (cur === target) return true;
    try {
      return cur.endsWith(target) || target.endsWith(cur);
    } catch (_) {
      return cur.indexOf(target) !== -1 || target.indexOf(cur) !== -1;
    }
  }

  on(event: string, cb: Listener) {
    const set = this.listeners.get(event) ?? new Set<Listener>();
    set.add(cb);
    this.listeners.set(event, set);
    return () => this.off(event, cb);
  }

  off(event: string, cb: Listener) {
    const set = this.listeners.get(event);
    if (!set) return;
    set.delete(cb);
    if (set.size === 0) this.listeners.delete(event);
  }

  emit(event: string, ...args: unknown[]) {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of Array.from(set)) {
      try {
        cb(...args);
      } catch (e) {
        /* ignore listener errors */
      }
    }
  }
}

// Export a singleton instance for app-wide use
export const audioPlayer = new AudioPlayerService();
